import type { AvailableDocument } from "@/services/api/case-checking";

const GENERIC_MIME_SUBTYPES = new Set(["octet-stream", "binary", "x-msdownload"]);
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function looksLikeOpaqueDocumentId(value: string, documentId: string): boolean {
  const v = value.trim();
  if (!v) return true;
  if (v === documentId) return true;
  if (UUID_RE.test(v)) return true;
  if (!v.includes(".") && v.length >= 20 && /^[0-9a-zA-Z_-]+$/.test(v)) return true;
  return false;
}

/** Human-readable name for CRM catalogue rows and queue labels. */
export function availableDocumentQueueLabel(doc: AvailableDocument): string {
  const id = doc.document_id;
  const filename = doc.filename?.trim() ?? "";
  const subject = doc.subject?.trim() ?? "";

  if (filename && !looksLikeOpaqueDocumentId(filename, id)) return filename;
  if (subject && !looksLikeOpaqueDocumentId(subject, id)) return subject;
  if (filename) return filename;
  if (subject) return subject;
  return id;
}

const MIME_TYPE_LABELS: Record<string, string> = {
  "application/pdf": "PDF",
  "application/msword": "Word",
  "application/vnd.ms-excel": "Excel",
  "application/vnd.ms-powerpoint": "PowerPoint",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "Word",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "Excel",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": "PowerPoint",
  "application/vnd.oasis.opendocument.text": "Document",
  "application/vnd.oasis.opendocument.spreadsheet": "Spreadsheet",
  "text/plain": "Text",
  "text/csv": "CSV",
  "text/html": "HTML",
  "image/jpeg": "JPEG",
  "image/jpg": "JPEG",
  "image/png": "PNG",
  "image/gif": "GIF",
  "image/webp": "WebP",
  "image/tiff": "TIFF",
  "message/rfc822": "Email",
  "application/zip": "ZIP",
  "application/x-zip-compressed": "ZIP",
};

function extensionFromFilename(filename: string | undefined): string | null {
  if (!filename?.includes(".")) return null;
  const ext = filename.split(".").pop()?.trim().toUpperCase();
  if (!ext || ext.length > 8) return null;
  return ext;
}

/** User-facing file type hint for CRM catalogue rows. */
export function fileTypeLabel(doc: AvailableDocument): string | null {
  const mime = doc.mime_type?.trim().toLowerCase();
  const fromName = extensionFromFilename(doc.filename || doc.subject);

  if (mime) {
    const known = MIME_TYPE_LABELS[mime];
    if (known) return known;

    if (fromName) return fromName;

    const subtype = mime.split("/").pop() ?? "";
    const isReadableSubtype =
      subtype.length > 0 &&
      subtype.length <= 12 &&
      !subtype.includes("openxml") &&
      !subtype.startsWith("vnd.") &&
      !GENERIC_MIME_SUBTYPES.has(subtype);

    if (isReadableSubtype) return subtype.toUpperCase();
    return "Document";
  }

  return fromName;
}

/** Format byte size for display (binary units). */
export function formatBytes(bytes: number): string {
  if (bytes < 0) return "";
  if (bytes === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let v = bytes;
  let u = 0;
  while (v >= 1024 && u < units.length - 1) {
    v /= 1024;
    u += 1;
  }
  return `${v < 10 && u > 0 ? v.toFixed(1) : Math.round(v)} ${units[u]}`;
}

export function formatAvailableDocumentMeta(
  doc: AvailableDocument,
  formatDateTime: (iso: string | null) => string,
): string | null {
  const parts: string[] = [];
  const typeLabel = doc.document_type_label?.trim();
  if (typeLabel) parts.push(typeLabel);

  if (doc.file_size != null && doc.file_size >= 0) {
    parts.push(formatBytes(doc.file_size));
  } else {
    const typeHint = fileTypeLabel(doc);
    if (typeHint) parts.push(typeHint);
  }

  const dated = formatDateTime(doc.document_date);
  if (dated) parts.push(dated);

  return parts.length > 0 ? parts.join(" · ") : null;
}
